import { useSortable } from '@dnd-kit/sortable';
import React__default, { useEffect } from 'react';
import { DragItemContainer } from './containers.js';
import { RemoveButton, DragHandle } from './controls.js';
import { StyledItem, StyledControls } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';

const DragItem = ({
  animateLayoutChanges,
  color,
  disabled,
  getNewIndex,
  handle,
  horizontal,
  id,
  index,
  renderItem,
  onRemove,
  style,
  useDragOverlay,
  wrapperStyle
}) => {
  const {
    active,
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
    getNewIndex
  });
  return jsx(Item, {
    ref: setNodeRef,
    value: id,
    color: color,
    disabled: disabled,
    dragging: isDragging,
    sorting: isSorting,
    handle: handle,
    handleProps: handle ? {
      ref: setActivatorNodeRef
    } : {},
    horizontal: horizontal,
    renderItem: renderItem,
    index: index,
    style: style({
      index,
      id,
      isDragging,
      isSorting,
      overIndex
    }),
    transform: transform,
    transition: transition,
    listeners: listeners,
    "data-index": index,
    "data-id": id,
    dragOverlay: !useDragOverlay && isDragging,
    onRemove: onRemove ? () => onRemove(id) : undefined,
    wrapperStyle: wrapperStyle === null || wrapperStyle === void 0 ? void 0 : wrapperStyle({
      index,
      isDragging,
      active,
      id
    }),
    ...attributes
  });
};
const Item = React__default.memo(React__default.forwardRef(({
  color,
  disabled,
  dragging,
  dragOverlay,
  handle,
  handleProps,
  horizontal,
  fadeIn,
  index,
  listeners,
  onRemove,
  renderItem,
  sorting,
  style,
  transform,
  transition,
  value,
  wrapperStyle,
  ...props
}, ref) => {
  useEffect(() => {
    if (!dragOverlay) {
      return;
    }
    document.body.style.cursor = 'grabbing';
    return () => {
      document.body.style.cursor = '';
    };
  }, [dragOverlay]);
  return jsx(DragItemContainer, {
    ref: ref,
    color: color,
    dragOverlay: dragOverlay,
    fadeIn: fadeIn,
    index: index,
    sorting: sorting,
    style: wrapperStyle,
    transform: transform,
    transition: transition,
    children: renderItem ? renderItem({
      dragOverlay: Boolean(dragOverlay),
      dragging: Boolean(dragging),
      handleProps,
      index,
      listeners,
      onRemove,
      ref,
      style,
      value
    }) : jsxs(StyledItem, {
      ...(!handle ? listeners : undefined),
      ...props,
      color: color,
      disabled: disabled,
      dragging: dragging,
      dragOverlay: dragOverlay,
      horizontal: horizontal,
      style: style,
      tabIndex: !handle ? 0 : undefined,
      withHandle: handle,
      children: [value, jsxs(StyledControls, {
        horizontal: horizontal,
        children: [onRemove && jsx(RemoveButton, {
          onClick: onRemove
        }), handle && jsx(DragHandle, {
          ...handleProps,
          ...listeners
        })]
      })]
    })
  });
}));

export { DragItem, Item };
